import type { NHovBranchId } from "../types";

// Kept short so a page close mid-animation does not strand state; the model
// commit is the ground truth.
export const NHOV_EXECUTION_SWAP_OUT_MS = 180;
export const NHOV_EXECUTION_REVEAL_MS = 160;
export const NHOV_EXECUTION_FAST_SWAP_OUT_MS = 80;
export const NHOV_EXECUTION_FAST_REVEAL_MS = 80;
export const NHOV_EXECUTION_ANIMATION_MS_NORMAL = NHOV_EXECUTION_SWAP_OUT_MS;
export const NHOV_EXECUTION_ANIMATION_MS_FAST = NHOV_EXECUTION_FAST_SWAP_OUT_MS;
export const NHOV_EXECUTION_ANIMATION_MS_NONE = 0;

export type ResetAnimationMode = "normal" | "fast" | "none";

export const DEFAULT_NHOV_RESET_ANIMATION_MODE: ResetAnimationMode = "normal";

export type NHovExecutionAnimationPhase = "swap-out" | "reveal";

export type NHovExecutionState = {
  readonly branchId: NHovBranchId;
  readonly phase: NHovExecutionAnimationPhase;
} | null;

export interface NHovExecutionAnimationTiming {
  readonly swapOutMs: number;
  readonly revealMs: number;
}

export function resolveNHovAnimationTiming(
  mode: ResetAnimationMode,
): NHovExecutionAnimationTiming {
  switch (mode) {
    case "fast":
      return {
        swapOutMs: NHOV_EXECUTION_FAST_SWAP_OUT_MS,
        revealMs: NHOV_EXECUTION_FAST_REVEAL_MS,
      };
    case "none":
      return {
        swapOutMs: NHOV_EXECUTION_ANIMATION_MS_NONE,
        revealMs: NHOV_EXECUTION_ANIMATION_MS_NONE,
      };
    case "normal":
    default:
      return {
        swapOutMs: NHOV_EXECUTION_SWAP_OUT_MS,
        revealMs: NHOV_EXECUTION_REVEAL_MS,
      };
  }
}

export function resolveNHovAnimationMs(mode: ResetAnimationMode): number {
  return resolveNHovAnimationTiming(mode).swapOutMs;
}

// Reentrancy guard for the swap-out → reveal window so a second hold cannot
// fire `executeNHov` twice. Pure logic — no DOM and no timers.
export class NHovExecutionController {
  private state: Exclude<NHovExecutionState, null> | null = null;

  tryStart(branchId: NHovBranchId): boolean {
    if (this.state !== null) return false;
    this.state = {
      branchId,
      phase: "swap-out",
    };
    return true;
  }

  setPhase(phase: NHovExecutionAnimationPhase): boolean {
    if (this.state === null) return false;
    this.state = {
      ...this.state,
      phase,
    };
    return true;
  }

  complete(): void {
    this.state = null;
  }

  isExecuting(): boolean {
    return this.state !== null;
  }

  getExecutingBranchId(): NHovBranchId | null {
    return this.state?.branchId ?? null;
  }

  getExecutingPhase(): NHovExecutionAnimationPhase | null {
    return this.state?.phase ?? null;
  }

  getState(): NHovExecutionState {
    return this.state === null ? null : { ...this.state };
  }
}
